import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Loader2, RefreshCw } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [captchaId, setCaptchaId] = useState("");
  const [captchaQ, setCaptchaQ] = useState("");
  const [captchaA, setCaptchaA] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refreshCaptcha = async () => {
    const { data } = await api.get("/auth/captcha");
    setCaptchaId(data.id); setCaptchaQ(data.question); setCaptchaA("");
  };
  useEffect(() => { refreshCaptcha(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await register({ ...form, role: "citizen", captcha_id: captchaId, captcha_answer: captchaA });
      toast.success("Account created!");
      nav("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      refreshCaptcha();
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 shadow-lg">
        <div className="tricolor-bar mb-6 -mx-8 -mt-8 rounded-t-xl overflow-hidden" />
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded bg-[#0F2942] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-semibold">PULB DRMS</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Citizen Portal</div>
          </div>
        </Link>
        <h1 className="text-2xl font-heading font-bold" data-testid="register-heading">Create your citizen account</h1>
        <p className="mt-1 text-sm text-slate-500">Pay taxes, view properties, download receipts.</p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
          <div>
            <Label>Full name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reg-phone" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reg-password" />
          </div>
          <div>
            <Label>Captcha</Label>
            <div className="flex gap-2 items-center">
              <div className="px-3 py-2 rounded-md bg-slate-100 dark:bg-slate-800 border font-mono text-sm flex-1 select-none">{captchaQ || "…"}</div>
              <Input required value={captchaA} onChange={(e) => setCaptchaA(e.target.value)} className="w-24" data-testid="reg-captcha" />
              <Button type="button" variant="ghost" size="icon" onClick={refreshCaptcha}><RefreshCw className="w-4 h-4" /></Button>
            </div>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full bg-[#0F2942] hover:bg-[#1E3A5F]" data-testid="btn-register-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
          </Button>
        </form>
        <p className="mt-6 text-sm text-slate-500 text-center">
          Already have an account? <Link to="/login" className="text-[#0F2942] dark:text-slate-200 font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
